import {
  Button,
  Container,
  Stack,
  Text,
  Title,
  Anchor,
  Alert,
  Image,
  PasswordInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { IconAlertCircle, IconLock, IconCheck } from "@tabler/icons-react";
import { URL } from "../../services/url";
import axios from "axios";

interface ResetPasswordResponse {
  success: boolean;
  response: string;
}

const ResetPassword = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (!tokenFromUrl) {
      setError("Invalid reset link. Please request a new password reset.");
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const validatePassword = (pwd: string) => {
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[\w!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/;
    return passwordPattern.test(pwd);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!validatePassword(password)) {
      setError("Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character");
      return;
    }

    if (!token) {
      setError("Invalid reset token. Please request a new password reset.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post<ResetPasswordResponse>(`${URL}reset-password/`, {
        token,
        password,
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(true);
        // Redirect to signin after 3 seconds
        setTimeout(() => {
          navigate("/signin");
        }, 3000);
      } else {
        setError(response.data.response || "Failed to reset password");
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error("Reset password failed", error);
      if (error.response?.data?.response) {
        setError(error.response.data.response);
      } else {
        setError("An error occurred. Please try again later.");
      }
    }
  };

  if (success) {
    return (
      <Container
        size={isMobile ? "xs" : "sm"}
        px={isMobile ? "md" : "xl"}
        py={isMobile ? "xl" : "80px"}
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Stack gap="xl" align="center" w="100%" maw={400} mx="auto">
          <Stack align="center" gap="md" ta="center">
            <Image 
              src="/landingimg.png" 
              maw={isMobile ? 120 : 150} 
            />
            <Stack gap="xs" align="center">
              <Title order={1} size={isMobile ? "h2" : "h1"} fw={700}>
                Kabbie
              </Title>
            </Stack>
          </Stack>

          <Alert
            icon={<IconCheck size={20} />}
            color="green"
            variant="light"
            radius="md"
            w="100%"
          >
            <Stack gap="xs">
              <Text fw={600}>Password Reset Successful</Text>
              <Text size="sm">
                Your password has been reset successfully. You will be redirected to the sign-in page in a few seconds.
              </Text>
            </Stack>
          </Alert>

          <Stack gap="md" w="100%" ta="center">
            <Button
              onClick={() => navigate("/signin")}
              variant="gradient"
              gradient={{ from: '#000000', to: '#333333', deg: 145 }}
            >
              Go to Sign In
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container
      size={isMobile ? "xs" : "sm"}
      px={isMobile ? "md" : "xl"}
      py={isMobile ? "xl" : "80px"}
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Stack gap="xl" align="stretch" w="100%" maw={400}>
        <Stack gap="md">
          <Image 
            src="/landingimg.png" 
            maw={isMobile ? 100 : 120} 
          />
          <Stack gap={4}>
            <Title order={1} size={isMobile ? "h2" : "h1"} fw={700}>
              Reset Your Password
            </Title>
            <Text size="sm" c="dimmed">
              Enter your new password below
            </Text>
          </Stack>
        </Stack>

        <Stack gap="lg" w="100%">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <PasswordInput
                label="New Password"
                placeholder="Enter your new password"
                required
                size="md"
                leftSection={<IconLock size={18} />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setError("")}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              <PasswordInput
                label="Confirm New Password"
                placeholder="Re-enter your new password"
                required
                size="md"
                leftSection={<IconLock size={18} />}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setError("")}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                  radius="md"
                >
                  <Text size="sm">{error}</Text>
                </Alert>
              )}

              <Button
                fullWidth
                size="lg"
                mt="md"
                type="submit"
                variant="gradient"
                gradient={{ from: '#000000', to: '#333333', deg: 145 }}
                loading={loading}
                loaderProps={{ type: "oval" }}
                disabled={!password || !confirmPassword || !token}
              >
                Reset Password
              </Button>
            </Stack>
          </form>

          <Stack gap="sm" mt="md">
            <Anchor href="/signin" size="sm" c="dimmed">
              ← Back to Sign In
            </Anchor>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default ResetPassword;